import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  TotpMultiFactorGenerator,
  MultiFactorAssertion,
  MultiFactorInfo,
  User,
  signOut
} from 'firebase/auth';
// @ts-ignore
import { auth } from '../../firebase';
import {
  getDocs,
  collection,
  where,
  query,
  getFirestore
} from 'firebase/firestore';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore
import CompanyLogo from '../../assets/company-logo.png';
import { useSelector, useDispatch } from 'react-redux';
// @ts-ignore
import { loadUser } from '../../hooks/authenticated';
import { useRecaptcha } from './recaptcha';

type Method = 'sms' | 'totp' | null;

const MFASignIn: React.FC = () => {
  const { recaptchaContainerRef, recaptcha, ready, reset } = useRecaptcha();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // @ts-ignore
  const { mfaResolver: resolver } = useSelector(state => state?.User);

  // Factor hints (fallback to empty array when resolver is missing)
  const phoneHints = useMemo(
    () =>
      (resolver?.hints ?? []).filter(
        (h: MultiFactorInfo) =>
          h.factorId === PhoneMultiFactorGenerator.FACTOR_ID
      ),
    [resolver]
  );
  const totpHints = useMemo(
    () =>
      (resolver?.hints ?? []).filter(
        (h: MultiFactorInfo) =>
          h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      ),
    [resolver]
  );

  const hasSMS = phoneHints.length > 0;
  const hasTOTP = totpHints.length > 0; // ← fixed syntax
  const hasBoth = hasSMS && hasTOTP;

  // UI state
  const [requiresReauth, setRequiresReauth] = useState(false);
  const [active, setActive] = useState<Method>(null);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  // SMS flow
  const [verificationId, setVerificationId] = useState<string>('');
  //const [smsCode, setSmsCode] = useState('');
  const [smsSent, setSmsSent] = useState(false);
  const smsCodeRef = useRef<HTMLInputElement>(null);

  // @ts-ignore
  //const [totpCode, setTotpCode] = useState('');
  const totpRef = useRef<HTMLInputElement>(null);

  function clearBanners() {
    setStatus('');
    setError('');
  }

  const completeLogin = async (user: User) => {
    const db = getFirestore();
    await user.getIdToken(true);
    const decodedToken = await auth.currentUser?.getIdTokenResult();

    let homePath = '/';

    if (decodedToken?.claims?.role) {
      const rolesQuery = query(
        collection(db, 'authorised_roles'),
        where('role', '==', decodedToken.claims.role)
      );

      const rolesSnapshot = await getDocs(rolesQuery);
      const roles: any[] = [];
      rolesSnapshot.forEach(doc => roles.push(doc.data()));

      if (roles.length === 1 && roles[0].homePath?.trim().length > 0) {
        homePath = roles[0].homePath;
      }
    }

    await loadUser({
      source: 'mfa-signin',
      user,
      selectedOrganisationId: 0,
      dispatch
    });

    navigate(homePath);
  };

  // If resolver missing (direct navigation), bounce back to login (or custom page)
  useEffect(() => {
    if (!resolver) {
      const custom = (location.state as any)?.customLoginPage as
        | string
        | undefined;
      navigate(custom ?? '/admin', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver]);

  // Auto-select behavior if only one factor
  useEffect(() => {
    if (!hasBoth) {
      setActive(hasSMS ? 'sms' : hasTOTP ? 'totp' : null);
    }
  }, [hasBoth, hasSMS, hasTOTP]);

  // Send SMS challenge
  // make sure you have this in your component setup:
  // const { containerRef: recaptchaContainerRef, recaptcha, ready, reset } = useRecaptcha();
  // and render: <div ref={recaptchaContainerRef} id="recaptcha-container" className="hidden sr-only" />

  const sendSms = async () => {
    if (!resolver) return; // guard

    try {
      clearBanners();
      setBusy(true);

      // Ensure the hook's verifier is ready
      let appVerifier = recaptcha;
      if (!appVerifier || !ready) {
        // Try to reset/re-init once
        await reset();
        appVerifier = recaptcha;
        if (!appVerifier || !ready) {
          setError('reCAPTCHA is not ready yet. Please try again in a moment.');
          return;
        }
      }

      // Optional: force a fresh token; ignore if it throws (verifyPhoneNumber will challenge again)
      try {
        await appVerifier.verify();
      } catch {}

      const hint = phoneHints[0];
      if (!hint) throw new Error('No phone factor available.');

      const provider = new PhoneAuthProvider(auth);
      const vid = await provider.verifyPhoneNumber(
        { multiFactorHint: hint, session: resolver.session },
        appVerifier
      );

      setVerificationId(vid);
      setSmsSent(true);
      setStatus('SMS sent. Enter the code below.');
      setTimeout(() => smsCodeRef.current?.focus(), 0);
    } catch (err) {
      console.error(err);
      if (err?.code === 'auth/invalid-app-credential') {
        setError(
          'Security check failed or expired. Please stay on this page and try again.'
        );
      } else if (err?.code === 'auth/captcha-check-failed') {
        setError('reCAPTCHA check failed. Please try again.');
      } else if (err?.code === 'auth/requires-recent-login') {
        setRequiresReauth(true);
      } else {
        setError(err?.message || 'Failed to send SMS.');
      }
    } finally {
      setBusy(false);
    }
  };

  // Resolve SMS MFA
  const verifySms = async (code?: string) => {
    if (!resolver) return; // guard
    try {
      clearBanners();
      setBusy(true);
      if (!verificationId) throw new Error('Missing verification ID');

      const smsCode =
        (code && code.trim && code?.trim()) ||
        smsCodeRef.current?.value.trim() ||
        '';

      const cred = PhoneAuthProvider.credential(verificationId, smsCode);
      const assertion: MultiFactorAssertion = PhoneMultiFactorGenerator.assertion(
        cred
      );

      const userCred = await resolver.resolveSignIn(assertion);
      setStatus('✅ Login successful!');
      // Redirect to your app/home here if desired
      console.log('Logged in:', userCred.user);
      await completeLogin(userCred.user);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Error verifying SMS code.');
    } finally {
      setBusy(false);
    }
  };

  // Resolve TOTP MFA
  const verifyTotp = async () => {
    if (!resolver) return; // guard
    try {
      setError('');
      setStatus('');
      setBusy(true);

      if (!totpRef.current?.value) throw new Error('Enter the 6-digit code.');

      // Get the enrolled TOTP factor's enrollmentId (uid) from hints
      const totpHint = resolver.hints.find(
        (h: MultiFactorInfo) =>
          h.factorId === TotpMultiFactorGenerator.FACTOR_ID
      );
      if (!totpHint) throw new Error('No authenticator factor found.');

      const assertion: MultiFactorAssertion = TotpMultiFactorGenerator.assertionForSignIn(
        totpHint.uid,
        totpRef.current?.value
      );

      const userCred = await resolver.resolveSignIn(assertion);
      setStatus('✅ Login successful!');
      await completeLogin(userCred.user);
    } catch (error) {
      if (error.code === 'auth/invalid-verification-code') {
        setError('Invalid authenticator code. Please try again');
      } else {
        console.error(error);
        setError(error?.message || 'Error verifying authenticator code.');
      }
    } finally {
      setBusy(false);
    }
  };

  // some mfa actions require a recent login
  const loginAgain = async () => {
    signOut(auth)
      .then(() => {
        // Sign-out successful.
        if (location?.state?.customLoginPage) {
          navigate(location.state.customLoginPage);
          return;
        }

        navigate('/admin');
      })
      .catch(error => {
        console.log(error);
      });
  };

  // Icons (inline SVGs)
  const PhoneIcon = () => (
    <svg
      className="h-8 w-8 text-indigo-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M2.25 6.75c0 8.284 6.716 15 15 15h1.5a1.5 1.5 0 001.5-1.5v-2.25a1.5 1.5 0 00-1.5-1.5h-2.25a1.5 1.5 0 00-1.5 1.5v.09a11.25 11.25 0 01-9.84-9.84h.09a1.5 1.5 0 001.5-1.5V3.75A1.5 1.5 0 006.75 2.25H4.5A1.5 1.5 0 003 3.75v3z"
      />
    </svg>
  );

  const ShieldIcon = () => (
    <svg
      className="h-8 w-8 text-emerald-600"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      aria-hidden="true">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M12 3l7.5 3v5.25c0 4.28-2.99 8.1-7.5 9-4.51-.9-7.5-4.72-7.5-9V6L12 3z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.5"
        d="M9.75 12.75l1.5 1.5 3-3"
      />
    </svg>
  );

  const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
      {children}
    </div>
  );

  const TileButton: React.FC<{
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    onClick: () => void;
    testId?: string;
  }> = ({ title, subtitle, icon, onClick, testId }) => (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-full text-left p-4 border rounded-md transition shadow-sm border-gray-200 hover:border-indigo-300 hover:shadow">
      <div className="flex items-start gap-4">
        <div className="shrink-0">{icon}</div>
        <div className="flex-1">
          <div className="text-base font-semibold text-gray-900">{title}</div>
          <div className="text-sm text-gray-600 mt-1">{subtitle}</div>
        </div>
      </div>
    </button>
  );

  return (
    <div
      className="min-h-full flex flex-col py-12 sm:px-6 lg:px-8"
      data-testid="mfa-signin">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          className="mx-auto h-12 w-auto mb-6"
          src={CompanyLogo}
          alt="Company Logo"
        />
        <h2 className="mt-8 text-center text-3xl font-extrabold text-gray-900">
          Multi-Factor Verification
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Choose how to verify your sign-in and enter the code to continue.
        </p>
      </div>
      {requiresReauth ? (
        <div
          className="min-h-full flex flex-col py-12 sm:px-6 lg:px-8 bg-gray-50"
          data-testid="reauth-required">
          <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
            <img
              className="mx-auto h-12 w-auto mb-6"
              src={CompanyLogo}
              alt="Company Logo"
            />
            <h2 className="mt-8 text-3xl font-extrabold text-gray-900">
              Please re-login
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              For your security, we need you to sign in again before continuing
              your multi-factor login.
            </p>

            <div className="mt-6 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <button
                onClick={loginAgain}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                Return to Login
              </button>
            </div>

            <p className="mt-6 text-xs text-gray-400">
              You’ll be redirected back here after signing in again.
            </p>
          </div>
        </div>
      ) : null}
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card>
          {/* Status banners */}
          {error && (
            <div className="text-red-700 mb-3" data-testid="mfaError">
              {error}
            </div>
          )}
          {status && (
            <div className="text-green-700 mb-3" data-testid="mfaStatus">
              {status}
            </div>
          )}

          {/* If both factors → show choice tiles. If only one → no tiles. */}
          {!active && hasBoth && (
            <>
              <h3 className="text-sm font-medium text-gray-900 mb-3 text-center">
                Choose your authentication method
              </h3>
              <div className="grid gap-4 mb-6">
                <TileButton
                  title="Use SMS"
                  subtitle="Receive a code by text message."
                  icon={<PhoneIcon />}
                  onClick={() => {
                    setError('');
                    setStatus('');
                    setActive('sms');
                  }}
                  testId="chooseSms"
                />
                <TileButton
                  title="Use Authenticator"
                  subtitle="Enter a code from your authenticator app."
                  icon={<ShieldIcon />}
                  onClick={() => {
                    setError('');
                    setStatus('');
                    setActive('totp');
                  }}
                  testId="chooseTotp"
                />
              </div>
            </>
          )}

          {/* Back to choice (only when both are available and one is active) */}
          {hasBoth && active && (
            <button
              type="button"
              className="mb-4 text-sm text-gray-700 hover:bg-button-bg-hover hover:text-button-text-hover inline-flex items-center"
              onClick={() => {
                setActive(null);
                setStatus('');
                setError('');
              }}>
              <span className="mr-2">←</span> Back to methods
            </button>
          )}

          {/* SMS factor UI */}
          <div
            className={
              active === 'sms'
                ? 'block'
                : hasBoth
                ? 'hidden'
                : hasSMS
                ? 'block'
                : 'hidden'
            }>
            {hasSMS && (
              <div className="space-y-3">
                {!smsSent ? (
                  <>
                    <p className="text-sm text-gray-700">
                      Click the button below to receive a verification code by
                      SMS.
                    </p>
                    <button
                      onClick={sendSms}
                      disabled={busy || !resolver}
                      className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60"
                      data-testid="smsSendButton">
                      {busy ? 'Sending…' : 'Send SMS Code'}
                    </button>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700">
                      SMS code
                    </label>
                    <div className="mt-1 flex gap-2">
                      <input
                        id="txtSmsOTP"
                        ref={smsCodeRef}
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        placeholder="123456"
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            verifySms(
                              (e.currentTarget as HTMLInputElement).value
                            );
                          }
                        }}
                        className="appearance-none block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        data-testid="smsCodeInput"
                      />
                      <button
                        onClick={() => verifySms()}
                        disabled={busy}
                        className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60"
                        data-testid="smsVerifyButton">
                        {busy ? 'Verifying…' : 'Verify'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* TOTP factor UI */}
          <div
            className={
              active === 'totp'
                ? 'block'
                : hasBoth
                ? 'hidden'
                : hasTOTP
                ? 'block'
                : 'hidden'
            }>
            {hasTOTP && (
              <div className="space-y-3">
                <label className="block text-sm font-medium text-gray-700">
                  Authenticator code
                </label>
                <div className="mt-1 flex gap-2">
                  <input
                    key="txtOTP"
                    id="txtOTP"
                    ref={totpRef}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        verifyTotp();
                      }
                    }}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="123456"
                    className="flex-1 appearance-none block w-40 px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    data-testid="totpCodeInput"
                  />
                  <button
                    onClick={verifyTotp}
                    disabled={busy || !resolver}
                    className="px-4 py-2 rounded-md shadow-sm text-sm font-medium text-button-text bg-button-bg hover:bg-button-bg-hover hover:text-button-text-hover disabled:opacity-60"
                    data-testid="totpVerifyButton">
                    {busy ? 'Verifying…' : 'Verify'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>
      {/* reCAPTCHA anchor for SMS */}
      <div
        ref={recaptchaContainerRef}
        id="recaptcha-container"
        className="hidden sr-only"
      />
    </div>
  );
};

export default MFASignIn;
